import React, { Fragment, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { compose } from "redux";

// Redux Actions
import {
  toggleMailingDrawer,
  toggleTemplateDrawer,
} from "../../../common/UserDataReducer";

// Custom Components
import MailingListDrawer from "../Drawers/MailingListDrawer";
import TemplateDrawer from "../Drawers/TemplateDrawer";
import MailingListTab from "./Tabs/MailingListTab";
import Tabs from "./Tabs/Tabs";
import TemplatesTab from "./Tabs/TemplatesTab";

// Custom Hooks
import useMailingListForm, {
  RESET_MAILING_LIST_FORM,
  SET_MAILING_LIST_STATE_FROM_PAYLOAD,
} from "../Drawers/hooks/useMailingListForm";
import useTemplateForm, {
  RESET_TEMPLATE_FORM_STATE,
  SET_TEMPLATE_STATE_FROM_PAYLOAD,
} from "../Drawers/hooks/useTemplateForm";

// Constants
import { colorCodes } from "../constant/common";

// Apollo Client
import { gql, useQuery } from "@apollo/client";

// External Utilities
import { uploadClient } from "../../..";
import { deepCopy } from "../../../helper";

// Stylesheets
import "./LeftSection.scss";
import { sort } from "fast-sort";

// Constants
export const TAB_TEMPLATES = "Templates";
export const TAB_MAILING_LIST = "Mailing List";

// Graphql Related
export const GET_ALL_DOCUMENT_REQUEST_TEMPLATES = gql`
  query documentrequesttemplates {
    documentrequesttemplates {
      id
      title
      color
      templateDocuments {
        id
        documentName
        document
      }
    }
  }
`;
export const GET_ALL_MAILING_LIST = gql`
  query documentrequestmailinglist {
    documentrequestmailinglist {
      id
      name
      color
      mailingListUsersCount
    }
  }
`;

const LeftSection = () => {
  // Queries
  const { data: rawTemplateResponse, loading: isGetTemplateLoading } = useQuery(
    GET_ALL_DOCUMENT_REQUEST_TEMPLATES,
    {
      client: uploadClient,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    }
  );

  const { data: rawMailingListResponse, loading: isGetMailingListLoading } =
    useQuery(GET_ALL_MAILING_LIST, {
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    });

  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = React.useState(TAB_TEMPLATES);
  const { templateState, onChange: onChangeForTemplate } = useTemplateForm();
  const { mailingListState, onChange: onChangeForMailingList } =
    useMailingListForm();

  // Template related local states
  const isMailingDrawerOpen = useSelector((state) => state.isMailingDrawerOpen);
  const [isEditTemplate, setIsEditTemplate] = useState(false);
  const formattedTemplateData = formatTemplateResponse(
    rawTemplateResponse?.documentrequesttemplates ?? []
  );
  const isTemplateDrawerOpen = useSelector(
    (state) => state.isTemplateDrawerOpen
  );
  // Mailing List state
  const [isEditMailingList, setIsEditMailingList] = useState(false);
  const formattedMailingListData = formatMailingListResponse(
    rawMailingListResponse?.documentrequestmailinglist ?? []
  );

  // Handlers
  const handleTabChange = (newTabValue) => {
    setActiveTab(newTabValue);
  };
  // Template related
  const handleTemplateEdit = (template) => {
    onChangeForTemplate(SET_TEMPLATE_STATE_FROM_PAYLOAD, {
      ...template,
      initialStateForEdit: deepCopy(template),
    });
    setIsEditTemplate(true);
    compose(dispatch, toggleTemplateDrawer)(true);
  };
  const templateOnCloseHandler = (isEdit = false) => {
    if (isEdit) {
      setIsEditTemplate(false);
    }
    compose(dispatch, toggleTemplateDrawer)(false);
    onChangeForTemplate(RESET_TEMPLATE_FORM_STATE);
  };

  // MailingList Related
  const handleMailingListEdit = (mailingList) => {
    onChangeForMailingList(SET_MAILING_LIST_STATE_FROM_PAYLOAD, {
      ...mailingList,
      initialMailingListStateForEdit: deepCopy(mailingList),
    });
    setIsEditMailingList(true);
    compose(dispatch, toggleMailingDrawer)(true);
  };

  const mailingListOnCloseHandler = (isEdit = false) => {
    if (isEdit) {
      setIsEditMailingList(false);
    }
    compose(dispatch, toggleMailingDrawer)(false);
    onChangeForMailingList(RESET_MAILING_LIST_FORM);
  };

  return (
    <Fragment>
      <section className=" left-section-grid-container">
        <Tabs currentTab={activeTab} onTabChange={handleTabChange} />
        <div>
          {activeTab === TAB_TEMPLATES && (
            <TemplatesTab
              isLoading={isGetTemplateLoading}
              templates={formattedTemplateData}
              handleTemplateEdit={handleTemplateEdit}
            />
          )}
          {!(activeTab === TAB_TEMPLATES) && (
            <MailingListTab
              isLoading={isGetMailingListLoading}
              mailingLists={formattedMailingListData}
              handleMailingListEdit={handleMailingListEdit}
            />
          )}
        </div>
        <div className=" d-flex align-items-center px-8">
          {activeTab === TAB_TEMPLATES && (
            <button
              className="primary-button"
              onClick={() => compose(dispatch, toggleTemplateDrawer)(true)}
            >
              Create Template
            </button>
          )}
          {!(activeTab === TAB_TEMPLATES) && (
            <button
              className="primary-button"
              onClick={() => compose(dispatch, toggleMailingDrawer)(true)}
            >
              Create Mailing List
            </button>
          )}
        </div>
      </section>
      <TemplateDrawer
        isOpen={isTemplateDrawerOpen}
        onClose={templateOnCloseHandler}
        isEdit={isEditTemplate}
        onChange={onChangeForTemplate}
        templateState={templateState}
      />
      <MailingListDrawer
        isOpen={isMailingDrawerOpen}
        onClose={mailingListOnCloseHandler}
        isEdit={isEditMailingList}
        onChange={onChangeForMailingList}
        mailingListState={mailingListState}
      />
    </Fragment>
  );
};

export default LeftSection;

// Helper functions

/**
 * Formats a template response for easier consumption.
 *
 * @param {Array} response - The array of templates to be formatted.
 * @returns {Array} An array of formatted template objects.
 */
export function formatTemplateResponse(response = []) {
  if (Array.isArray(response)) {
    return sort(
      response.map((template) => ({
        templateId: template.id,
        templateTitle: template.title,
        colorCode: getColorCodeByLabel(template.color),
        requiredDocuments: sort(
          template.templateDocuments.map((document) => ({
            documentId: document.id,
            documentName: document.documentName,
            documentFile: typeof document?.document === "string" ? null : -1,
            documentFileName: document?.document?.split("/")?.pop(), // Extracting the file name from the path
          }))
        ).asc((u) => u.documentName),
      }))
    ).asc((u) => u.templateTitle?.toLowerCase());
  }

  return [];
}

/**
 * Gets the color code based on the provided label.
 *
 * @param {string} label - The label to find the color code for.
 * @returns {{ label: string, colorCode: string }} The color object containing the label and color code.
 */
function getColorCodeByLabel(label) {
  const foundColor = colorCodes.find((color) => color.label === label);
  return foundColor ? foundColor : { label: "Primary", colorCode: "#6366F1" };
}

/**
 * Formats a raw mailing list response for easier consumption.
 *
 * @param {Array} rawMailingListResponse - The array of raw mailing lists to be formatted.
 * @returns {Array} An array of formatted mailing list objects.
 */
export function formatMailingListResponse(rawMailingListResponse = []) {
  return rawMailingListResponse.map((rawMailingList) => {
    const {
      id,
      name: mailingListTitle,
      color,
      documentrequestmailinglistuserSet = [],
      mailingListUsersCount = 0,
    } = rawMailingList;

    const { label: colorLabel, colorCode } = getColorCodeByLabel(color);

    const users = documentrequestmailinglistuserSet.map((user) => ({
      email: user.email,
      name: user.name,
    }));

    return {
      mailingListId: id,
      mailingListTitle,
      colorCode: { label: colorLabel, colorCode },
      users,
      mailingListUsersCount,
    };
  });
}
